import { useIsMobile } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useRef, useState } from 'react'
import { TabItem } from '../Tabs.types'

type PaginatedItems = {
  pages: TabItem[][]
  totalPages: number
}

type UsePaginatedItemsProps = {
  items: TabItem[]
  onItemSelect: (tab: TabItem) => void
  selectedItem: TabItem | null
  fontSize: number
  itemSpacing: number
  containerElement: HTMLElement | null
}

// Average character width for Barlow font (in em units)
const BARLOW_AVG_CHAR_WIDTH = 0.5

function usePaginatedItems({
  items,
  onItemSelect,
  selectedItem,
  fontSize,
  itemSpacing,
  containerElement
}: UsePaginatedItemsProps) {
  const [paginatedItems, setPaginatedItems] = useState<PaginatedItems>({
    pages: [],
    totalPages: 0
  })

  const prevWidth = useRef(0)

  const [currentPage, setCurrentPage] = useState(0)

  const isMobile = useIsMobile()

  const estimateItemWidth = useCallback(
    (item: TabItem): number => {
      return (item?.des ?? '').length * BARLOW_AVG_CHAR_WIDTH * fontSize
    },
    [fontSize]
  )

  const paginateItemsFit = useCallback(
    (
      containerWidth: number,
      itemSpacing: number,
      items: TabItem[]
    ): PaginatedItems => {
      const pages: TabItem[][] = []
      let currentPage: TabItem[] = []
      let totalWidth = 0

      for (let i = 0; i < items.length; i++) {
        const itemWidth = estimateItemWidth(items[i])
        const itemTotalWidth = itemWidth + itemSpacing + (i % 2 === 1 ? 2 : 0)

        if (totalWidth + itemTotalWidth <= containerWidth) {
          currentPage.push(items[i])
          totalWidth += itemTotalWidth
        } else {
          if (currentPage.length > 0) {
            pages.push(currentPage)
          }
          currentPage = [items[i]]
          totalWidth = itemTotalWidth
        }
      }

      if (currentPage.length > 0) {
        pages.push(currentPage)
      }

      return {
        pages,
        totalPages: pages.length
      }
    },
    [estimateItemWidth]
  )

  useEffect(() => {
    const updatePagination = () => {
      if (containerElement) {
        const newWidth = containerElement.offsetWidth

        const totalItemsLength = paginatedItems.pages.flatMap(
          (page) => page
        ).length

        if (
          newWidth !== prevWidth.current ||
          items.length !== totalItemsLength
        ) {
          prevWidth.current = newWidth
          const result = paginateItemsFit(newWidth, itemSpacing, items)
          setPaginatedItems(result)

          const currentSelectedItemPageIndex = result.pages.findIndex((page) =>
            page.find((item) => item.uuid === selectedItem?.uuid)
          )

          if (currentSelectedItemPageIndex !== -1) {
            setCurrentPage(
              currentSelectedItemPageIndex !== -1
                ? currentSelectedItemPageIndex
                : 0
            )
          }
        }
      }
    }

    updatePagination()

    const resizeObserver = new ResizeObserver(updatePagination)

    if (containerElement) {
      resizeObserver.observe(containerElement)
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement)
      }
    }
  }, [
    items,
    itemSpacing,
    paginateItemsFit,
    containerElement,
    selectedItem,
    paginatedItems.pages
  ])

  const goToNextPage = useCallback(() => {
    const { totalPages, pages } = paginatedItems
    setCurrentPage((prevPage) => {
      const page = prevPage < totalPages - 1 ? prevPage + 1 : prevPage

      const items = pages[page]

      if (isMobile && items?.[0]?.uuid) {
        onItemSelect(items[0])
      }

      return page
    })
  }, [paginatedItems, isMobile, onItemSelect])

  const goToPreviousPage = useCallback(() => {
    const { pages } = paginatedItems
    setCurrentPage((prevPage) => {
      const page = prevPage > 0 ? prevPage - 1 : prevPage

      const items = pages[page]

      if (isMobile && items?.[0]?.uuid) {
        onItemSelect(items[0])
      }

      return page
    })
  }, [paginatedItems, isMobile, onItemSelect])

  const displayArrows = paginatedItems.totalPages > 1

  const isPrevArrowDisabled = currentPage === 0
  const isNextArrowDisabled = currentPage === paginatedItems.totalPages - 1

  return {
    currentPageItems: paginatedItems.pages[currentPage] || [],
    totalPages: paginatedItems.totalPages,
    goToNextPage,
    goToPreviousPage,
    displayArrows,
    isPrevArrowDisabled,
    isNextArrowDisabled
  }
}

export default usePaginatedItems
